import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import FocusLock from 'react-focus-lock';

import {
  PageScrollStyles,
  DrawerContainer,
  DrawerContent,
  Overlay,
} from './styles';

interface Props {
  children: any;
  drawerOpen: boolean;
  setDrawerOpen: (...args: unknown[]) => void;
}

const Drawer = ({ children, drawerOpen = false, setDrawerOpen }: Props) => {
  // Get the div that will be used as a portal
  const portalRoot = typeof document !== 'undefined' && document.getElementById('portal');

  // User hits "esc" button
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setDrawerOpen(false);
    }
  }, [setDrawerOpen]);

  // Get HTML tag of the page
  const root = typeof document !== "undefined" ? document?.documentElement : undefined;

  // Set page to no scroll when drawer active
  useEffect(() => {
    if (drawerOpen) {
      root?.classList.add('js-drawer-open');
    } else {
      root?.classList.remove('js-drawer-open');
    }
  }, [drawerOpen, root?.classList]);

  // Add keyboard listener for closing blade
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
      // On unmount of the blade, enable page scrolling
      root?.classList.remove('js-drawer-open');
    };
  }, [handleKeyDown, root?.classList]);

  const portalContent = (
    <CSSTransition
      in={drawerOpen}
      timeout={{
        enter: 300,
        exit: 700,
      }}
      classNames="on-drawer"
      unmountOnExit
    >
      <>
        <DrawerContainer
          className="drawer-scroll-container"
          data-testid="drawer-container"
          onClick={(e) => e.stopPropagation()}
        >
          <PageScrollStyles />
          {/* Content */}
          <DrawerContent>
            <FocusLock returnFocus>{children}</FocusLock>
          </DrawerContent>
        </DrawerContainer>
        <Overlay
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-overlay"
        />
      </>
    </CSSTransition>
  );

  return portalRoot && createPortal(portalContent, portalRoot);
};

export default Drawer;
